import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import {
  useListOrgRawUploadsQuery,
  useListResourceImportSessionsQuery,
} from '../store/hooks';
import { MigrationImportsList } from './migration-imports-list';
import { OrganizationDataImportV2Section } from './organization-data-import-v2-section';
import { StagedResourcesSection } from './staged-resources-section';

interface DataImportSectionProps {
  organizationId: string;
}

/**
 * The v2 Data Import pipeline surface, inline in the org chrome and
 * URL-addressable (`dsession` query param selects a batch):
 *
 *   1. upload raw source files to the S3 landing zone,
 *   2. (outside the app) the migration agent converts them to resource
 *      envelopes and ingests each batch into the staging table,
 *   3. review a batch's staged resources here and commit them.
 */
export function DataImportSection({ organizationId }: DataImportSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('dsession');

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  if (sessionId) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => patch({ dsession: null })}
          >
            Back to imports
          </Button>
        </Box>
        <StagedResourcesSection
          organizationId={organizationId}
          sessionId={sessionId}
          showCommit
        />
      </Box>
    );
  }

  return <DataImportOverview organizationId={organizationId} onOpenBatch={id => patch({ dsession: id })} />;
}

interface DataImportOverviewProps {
  organizationId: string;
  onOpenBatch: (sessionId: string) => void;
}

function DataImportOverview({ organizationId, onOpenBatch }: DataImportOverviewProps) {
  const uploadsQuery = useListOrgRawUploadsQuery({ organizationId });
  const sessionsQuery = useListResourceImportSessionsQuery({ organizationId });

  const refresh = () => {
    void uploadsQuery.refetch();
    void sessionsQuery.refetch();
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload source files; the migration agent converts them into resource
        envelopes and stages each one for review and commit. Each source's
        progress shows in Imports below.
      </Typography>
      <OrganizationDataImportV2Section
        organizationId={organizationId}
        onUploaded={() => void uploadsQuery.refetch()}
      />
      <MigrationImportsList
        uploads={uploadsQuery.data?.uploads ?? []}
        sessions={sessionsQuery.data?.items ?? []}
        isLoading={uploadsQuery.isLoading || sessionsQuery.isLoading}
        onRefresh={refresh}
        onOpenBatch={onOpenBatch}
      />
    </Box>
  );
}
